import React, { useMemo } from 'react';
import { Box } from '@mui/material';

export type CodeLanguage = 'python' | 'json';

// VS Code Light+ colours, on a soft background that sits well on the light builder pages.
const COLORS = {
  background: '#f8fafc',
  text: '#1f2937',
  keyword: '#0000ff',
  control: '#af00db',
  type: '#267f99',
  field: '#001080',
  string: '#a31515',
  docstring: '#008000',
  comment: '#008000',
  number: '#098658',
  call: '#795e26',
} as const;

type TokenKind = keyof typeof COLORS;

const PYTHON = new RegExp(
  [
    '(?<docstring>"""[\\s\\S]*?"""|\'\'\'[\\s\\S]*?\'\'\')',
    '(?<string>"(?:\\\\.|[^"\\\\\\n])*"|\'(?:\\\\.|[^\'\\\\\\n])*\')',
    '(?<comment>#[^\\n]*)',
    '(?<classdef>\\bclass\\s+[A-Za-z_]\\w*)',
    '(?<field>^[ \\t]+[A-Za-z_]\\w*(?=[ \\t]*:))',
    '(?<control>\\b(?:from|import|as|return|if|else|elif|for|in|while|with|try|except|raise|pass|def|lambda|yield)\\b)',
    '(?<keyword>\\b(?:None|True|False|and|or|not|is)\\b|\\.\\.\\.)',
    '(?<type>\\b(?:str|int|float|bool|bytes|dict|list|set|tuple|List|Dict|Optional|Union|Any|Literal|Set|Tuple|BaseModel|datetime|date|time|UUID|Decimal|EmailStr|HttpUrl|Enum)\\b)',
    '(?<call>\\b[A-Za-z_]\\w*(?=\\())',
    '(?<number>\\b\\d+(?:\\.\\d+)?\\b)',
  ].join('|'),
  'gm',
);

const JSON_RE = new RegExp(
  [
    '(?<field>"(?:\\\\.|[^"\\\\])*"(?=\\s*:))',
    '(?<string>"(?:\\\\.|[^"\\\\])*")',
    '(?<keyword>\\b(?:true|false|null)\\b)',
    '(?<number>-?\\b\\d+(?:\\.\\d+)?(?:[eE][+-]?\\d+)?\\b)',
  ].join('|'),
  'g',
);

interface Token {
  text: string;
  kind?: TokenKind;
}

export function tokenize(code: string, language: CodeLanguage): Token[] {
  const re = new RegExp((language === 'python' ? PYTHON : JSON_RE).source, language === 'python' ? 'gm' : 'g');
  const out: Token[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(code)) !== null) {
    if (m[0] === '') {
      re.lastIndex += 1;
      continue;
    }
    const start = m.index;
    if (start > last) out.push({ text: code.slice(last, start) });
    const groups = m.groups || {};
    const kind = Object.keys(groups).find((k) => groups[k] !== undefined);
    if (kind === 'classdef') {
      const name = m[0].replace(/^class\s+/, '');
      out.push({ text: m[0].slice(0, m[0].length - name.length), kind: 'control' });
      out.push({ text: name, kind: 'type' });
    } else {
      out.push({ text: m[0], kind: kind as TokenKind });
    }
    last = start + m[0].length;
  }
  if (last < code.length) out.push({ text: code.slice(last) });
  return out;
}

interface CodeHighlightProps {
  code: string;
  language: CodeLanguage;
  maxHeight?: number | string;
  /** Wrap long lines (field descriptions) instead of scrolling sideways. */
  wrap?: boolean;
  fontSize?: string;
}

/** Read-only code with editor-style syntax colours. */
export const CodeHighlight: React.FC<CodeHighlightProps> = ({ code, language, maxHeight = 400, wrap = true, fontSize = '0.8rem' }) => {
  const tokens = useMemo(() => tokenize(code, language), [code, language]);
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        maxHeight,
        overflow: 'auto',
        borderRadius: 1.5,
        border: '1px solid #e2e8f0',
        bgcolor: COLORS.background,
        color: COLORS.text,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Courier New", monospace',
        fontSize,
        lineHeight: 1.6,
        whiteSpace: wrap ? 'pre-wrap' : 'pre',
        wordBreak: wrap ? 'break-word' : 'normal',
        tabSize: 4,
      }}
    >
      {tokens.map((t, i) =>
        t.kind ? (
          <span key={i} style={{ color: COLORS[t.kind] }}>
            {t.text}
          </span>
        ) : (
          <React.Fragment key={i}>{t.text}</React.Fragment>
        ),
      )}
    </Box>
  );
};

export default CodeHighlight;
